import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { api, clearToken, getToken, setToken, type ApiError } from "./api";

type AuthState =
  | { status: "checking" }
  | { status: "anonymous"; error?: string }
  | { status: "authed" };

interface AuthContextValue {
  state: AuthState;
  login: (token: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() =>
    getToken() ? { status: "checking" } : { status: "anonymous" },
  );

  // Verify any token we found in sessionStorage at boot.
  useEffect(() => {
    if (state.status !== "checking") return;
    let cancelled = false;
    api
      .whoami()
      .then(() => {
        if (!cancelled) setState({ status: "authed" });
      })
      .catch(() => {
        if (cancelled) return;
        clearToken();
        setState({ status: "anonymous" });
      });
    return () => {
      cancelled = true;
    };
  }, [state.status]);

  // Global 401 listener — any fetch error from useApi will dispatch this.
  useEffect(() => {
    const onUnauth = () => {
      clearToken();
      setState({ status: "anonymous", error: "Session expired. Sign in again." });
    };
    window.addEventListener("mailadmin:unauthorized", onUnauth);
    return () => window.removeEventListener("mailadmin:unauthorized", onUnauth);
  }, []);

  const login = useCallback(async (token: string) => {
    setToken(token.trim());
    try {
      await api.whoami();
      setState({ status: "authed" });
    } catch (e) {
      clearToken();
      const err = e as ApiError;
      setState({
        status: "anonymous",
        error: err.status === 401 ? "Invalid token." : err.error ?? "Login failed.",
      });
      throw e;
    }
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setState({ status: "anonymous" });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, login, logout }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// Helper for components that call the API directly — dispatches a global
// `mailadmin:unauthorized` event on 401 so AuthProvider can clear state.
export function reportApiError(err: unknown): void {
  const status = (err as { status?: number } | null)?.status;
  if (status === 401) {
    window.dispatchEvent(new Event("mailadmin:unauthorized"));
  }
}
